import { useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createMiscPurchaseOrderAmendment,
  deleteMiscPurchaseOrderAmendment,
  fetchMiscPurchaseOrderAmendmentDetail,
  submitMiscPurchaseOrderAmendment,
  updateMiscPurchaseOrderAmendment,
} from '#/api/srm/miscPurchaseOrder/amendment';
import { fetchMiscPurchaseOrderDetail } from '#/api/srm/miscPurchaseOrder/fetchDetail';
import { fetchMiscPurchaseOrderPaymentPlan } from '#/api/srm/miscPurchaseOrder/fetchPaymentPlan';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { useFormValidationSummary } from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import PurchaseAmendmentPaymentPlanEditor from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentPaymentPlanEditor';
import PurchaseAmendmentStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentStatusBadge';
import { computePurchaseOrderAmount } from '#/modules/srmMgmt/purchaseOrder/helper/formSerialize';
import {
  emptyPaymentPlanFormValues,
  mapPaymentPlanToFormValues,
  pickPaymentPlanForWrite,
  validatePaymentPlanBeforeSubmit,
} from '#/modules/srmMgmt/purchaseOrder/helper/paymentPlanForm';
import { PURCHASE_AMENDMENT_KIND } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentKind';
import {
  PURCHASE_AMENDMENT_STATUS,
  findOpenPurchaseAmendment,
  isPurchaseAmendmentDraft,
  pickPurchaseAmendmentUpdatedAt,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentStatus';
import { formatDisplayAmount } from '#/utils/formatNumber';
import {
  miscPurchaseOrderDetailQueryKey,
  miscPurchaseOrderListQueryKeyPrefix,
  purchaseAmendmentDetailQueryKey,
  purchasePaymentPlanQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import MiscPurchaseOrderLinesEditor from '../components/MiscPurchaseOrderLinesEditor';
import {
  getMiscPurchaseOrderApiErrorMessage,
  isMiscPurchaseOrderVersionConflict,
} from '../helper/apiError';
import { canAmendMiscPurchaseOrder } from '../helper/miscPurchaseOrderStatus';
import {
  MISC_PURCHASE_ORDER_AMENDMENT_FIELD_LABELS,
  miscPurchaseOrderAmendmentSchema,
} from '../schemas/miscPurchaseOrderAmendmentSchema';
import {
  MISC_PURCHASE_ORDER_ROUNDING_MODE_OPTIONS,
  MISC_PURCHASE_ORDER_TAX_TYPE_OPTIONS,
  mapMiscPurchaseOrderDetailToForm,
  pickMiscPurchaseOrderUpdatedAt,
} from '../schemas/miscPurchaseOrderSchema';

const KIND = PURCHASE_AMENDMENT_KIND.MISC_PURCHASE_ORDER;

/** BE `tax_rate` 為 fraction（0.05）→ 預覽用百分比。 */
function taxRateToPercent(taxRate) {
  const n = Number(taxRate);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return n > 0 && n <= 1 ? Math.round(n * 10000) / 100 : n;
}

/** 表單預覽金額（後端以保存結果為準）。 */
function computeMiscAmount(lines, taxType, taxRatePercent) {
  return computePurchaseOrderAmount(
    (lines ?? []).map((line) => ({ orderQty: line?.qty, unitPrice: line?.unitPrice })),
    taxType,
    taxRatePercent,
  );
}

/** 既有草稿 → 表單值（讀 `after` 快照；非庫存明細與 detail lines 同形）。 */
function mapAmendmentToForm(amendment, detail) {
  const after = amendment?.after ?? {};
  const base = mapMiscPurchaseOrderDetailToForm({
    ...detail,
    amountSummary: {
      ...detail?.amountSummary,
      taxType: after.taxType ?? detail?.amountSummary?.taxType,
      roundingMode: after.roundingMode ?? detail?.amountSummary?.roundingMode,
    },
    lines: after.lines ?? detail?.lines,
  });
  return {
    ...base,
    issueReason: amendment?.issueReason ?? '',
    remark: amendment?.remark ?? '',
  };
}

/**
 * 非庫存採購異動單：建立／編輯草稿，提交後經核准才套用。
 * 改變應付總額時須一併附完整付款計畫；不改供應商名稱、幣別、匯率。
 */
const CreateEditMiscPurchaseOrderAmendmentPage = () => {
  const { id, amendmentId } = useParams();
  const isEditMode = Boolean(amendmentId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.MISC_PURCHASE_ORDER_UPDATE);
  const amendmentUpdatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: miscPurchaseOrderDetailQueryKey(id),
    queryFn: () => fetchMiscPurchaseOrderDetail(id),
    enabled: Boolean(id),
  });
  const amendmentQuery = useQuery({
    queryKey: purchaseAmendmentDetailQueryKey(KIND, id, amendmentId),
    queryFn: () => fetchMiscPurchaseOrderAmendmentDetail(id, amendmentId),
    enabled: Boolean(id && amendmentId),
  });
  const paymentPlanQuery = useQuery({
    queryKey: purchasePaymentPlanQueryKey(KIND, id),
    queryFn: () => fetchMiscPurchaseOrderPaymentPlan(id),
    enabled: Boolean(id),
    retry: false,
  });

  const detail = detailQuery.data?.data ?? null;
  const amendment = amendmentQuery.data?.data;
  const currentPlan = paymentPlanQuery.data?.data ?? null;
  const currentPlanReady = !paymentPlanQuery.isLoading;
  const taxRatePercent = taxRateToPercent(detail?.amountSummary?.taxRate);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(miscPurchaseOrderAmendmentSchema),
    defaultValues: {
      ...mapMiscPurchaseOrderDetailToForm(null),
      paymentPlan: emptyPaymentPlanFormValues(),
    },
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const watchedTaxType = useWatch({ control, name: 'taxType' });
  const amountPreview = useMemo(
    () => computeMiscAmount(watchedLines, watchedTaxType, taxRatePercent),
    [watchedLines, watchedTaxType, taxRatePercent],
  );
  // 原單以同一套預覽公式計算，避免與後端 rounding 差異誤判為「總額已變」
  const originalPreview = useMemo(() => {
    if (!detail) {
      return null;
    }
    const original = mapMiscPurchaseOrderDetailToForm(detail);
    return computeMiscAmount(original.lines, original.taxType, taxRatePercent);
  }, [detail, taxRatePercent]);

  useEffect(() => {
    if (!detail || !currentPlanReady) {
      return;
    }
    if (isEditMode && !amendment) {
      return;
    }
    amendmentUpdatedAtRef.current = pickPurchaseAmendmentUpdatedAt(amendment);
    resetValidationSummary();
    reset({
      ...(isEditMode
        ? mapAmendmentToForm(amendment, detail)
        : { ...mapMiscPurchaseOrderDetailToForm(detail), issueReason: '', remark: '' }),
      paymentPlan: mapPaymentPlanToFormValues(
        isEditMode ? amendment?.paymentPlan : null,
        currentPlan,
      ),
    });
  }, [detail, amendment, currentPlan, currentPlanReady, isEditMode, reset, resetValidationSummary]);

  function buildWritePayload(values, updatedAt) {
    return {
      issueReason: values.issueReason,
      remark: values.remark,
      taxType: values.taxType,
      roundingMode: values.roundingMode,
      lines: values.lines,
      updatedAt,
      paymentPlan: pickPaymentPlanForWrite(values.paymentPlan),
    };
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, values }) => {
      if (action === 'delete') {
        return deleteMiscPurchaseOrderAmendment(id, amendmentId, {
          updatedAt: amendmentUpdatedAtRef.current,
        });
      }
      const saved = isEditMode
        ? await updateMiscPurchaseOrderAmendment(
          id,
          amendmentId,
          buildWritePayload(values, amendmentUpdatedAtRef.current),
        )
        : await createMiscPurchaseOrderAmendment(
          id,
          buildWritePayload(values, pickMiscPurchaseOrderUpdatedAt(detail)),
        );
      if (action !== 'submit') {
        return saved;
      }
      // 先保存草稿再提交；提交使用異動單自身最新版本
      amendmentUpdatedAtRef.current = pickPurchaseAmendmentUpdatedAt(saved?.data);
      return submitMiscPurchaseOrderAmendment(id, saved?.data?.id ?? amendmentId, {
        updatedAt: amendmentUpdatedAtRef.current,
      });
    },
    onSuccess: async (result, { action }) => {
      const fallbackMessage = {
        save: '異動草稿已儲存',
        submit: '異動單已提交，待審核',
        delete: '異動草稿已刪除',
      }[action];
      toast.success(result?.message || fallbackMessage);
      await queryClient.invalidateQueries({ queryKey: miscPurchaseOrderDetailQueryKey(id) });
      await queryClient.invalidateQueries({ queryKey: miscPurchaseOrderListQueryKeyPrefix() });

      if (action === 'save' && !isEditMode) {
        const createdId = result?.data?.id;
        if (createdId) {
          navigate(ROUTES.miscPurchaseOrderAmendmentEdit(id, createdId), { replace: true });
          return;
        }
      }
      if (action === 'save') {
        amendmentUpdatedAtRef.current = pickPurchaseAmendmentUpdatedAt(result?.data);
        await queryClient.invalidateQueries({
          queryKey: purchaseAmendmentDetailQueryKey(KIND, id, amendmentId),
        });
        return;
      }
      navigate(ROUTES.miscPurchaseOrderDetail(id), { replace: true });
    },
    onError: (error) => {
      toast.error(getMiscPurchaseOrderApiErrorMessage(error, '異動失敗'));
      if (isMiscPurchaseOrderVersionConflict(error)) {
        detailQuery.refetch();
        if (isEditMode) {
          amendmentQuery.refetch();
        }
      }
    },
  });

  const pending = commandMutation.isPending;
  const openAmendment = findOpenPurchaseAmendment(detail?.amendments);
  const isPageLoading = detailQuery.isLoading || (isEditMode && amendmentQuery.isLoading);
  const pageError = detailQuery.error || (isEditMode ? amendmentQuery.error : null);
  const currentPlanError = paymentPlanQuery.isError
    ? getMiscPurchaseOrderApiErrorMessage(paymentPlanQuery.error, '讀取付款計畫失敗')
    : null;

  if (detail && !canAmendMiscPurchaseOrder(detail.status)) {
    return <Navigate to={ROUTES.miscPurchaseOrderDetail(id)} replace />;
  }

  // 同單同時最多一張在途異動：新建時已有草稿就轉去編輯，已提交則回詳情
  if (!isPageLoading && detail && !isEditMode && openAmendment) {
    if (openAmendment.status === PURCHASE_AMENDMENT_STATUS.DRAFT && openAmendment.id) {
      return <Navigate to={ROUTES.miscPurchaseOrderAmendmentEdit(id, openAmendment.id)} replace />;
    }
    return <Navigate to={ROUTES.miscPurchaseOrderDetail(id)} replace />;
  }

  if (!isPageLoading && isEditMode && amendment && !isPurchaseAmendmentDraft(amendment)) {
    return <Navigate to={ROUTES.miscPurchaseOrderDetail(id)} replace />;
  }

  function onSave(values) {
    markSubmitAttempted();
    if (!pending) {
      commandMutation.mutate({ action: 'save', values });
    }
  }

  function onSubmitDraft(values) {
    markSubmitAttempted();
    if (pending) {
      return;
    }
    const planError = validatePaymentPlanBeforeSubmit(values.paymentPlan, {
      originalTotal: originalPreview?.totalAmount,
      nextTotal: amountPreview.totalAmount,
    });
    if (planError) {
      toast.error(planError);
      return;
    }
    commandMutation.mutate({ action: 'submit', values });
  }

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除異動草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (ok && !pending) {
      commandMutation.mutate({ action: 'delete' });
    }
  }

  return (
    <PageShell variant="formTall">
      {ConfirmDialogHost}
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">
                {isEditMode ? '編輯非庫存採購異動單' : '新建非庫存採購異動單'}
              </span>
              {isEditMode && amendment?.status ? (
                <PurchaseAmendmentStatusBadge status={amendment.status} size="sm" />
              ) : null}
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.miscPurchaseOrderDetail(id)}>返回詳情</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : null}
          {pageError ? (
            <p className="text-sm text-destructive">
              {getMiscPurchaseOrderApiErrorMessage(pageError, '載入非庫存採購單或異動單失敗')}
            </p>
          ) : null}

          {!isPageLoading && !pageError && detail ? (
            <form
              className="sheet"
              onSubmit={handleSubmit(onSave, markSubmitAttempted)}
              noValidate
            >
              <p className="mb-4 text-sm text-muted-foreground">
                {`供應商 ${detail.supplierName || '-'}，幣別 ${detail.currency?.code || '-'}。`}
                {isEditMode && amendment?.amendmentNo ? `異動號 ${amendment.amendmentNo}。` : ''}
                異動不能改這兩項；儲存為草稿，提交後經核准才套用。
              </p>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MISC_PURCHASE_ORDER_AMENDMENT_FIELD_LABELS}
                className="mb-4"
              />
              <div className="mb-3 grid grid-cols-1 gap-3 @4xl/sheet:grid-cols-2">
                <FloatingSelect
                  id="taxType"
                  label="稅別"
                  options={MISC_PURCHASE_ORDER_TAX_TYPE_OPTIONS}
                  control={control}
                  errors={errors}
                  isDisabled={pending}
                />
                <FloatingSelect
                  id="roundingMode"
                  label="進位"
                  options={MISC_PURCHASE_ORDER_ROUNDING_MODE_OPTIONS}
                  control={control}
                  errors={errors}
                  isDisabled={pending}
                />
                <FloatingTextarea
                  id="issueReason"
                  label="異動事由"
                  register={register}
                  errors={errors}
                  rows={2}
                  disabled={pending}
                />
                <FloatingTextarea
                  id="remark"
                  label="備註"
                  register={register}
                  errors={errors}
                  rows={2}
                  disabled={pending}
                />
              </div>
              <MiscPurchaseOrderLinesEditor
                control={control}
                register={register}
                errors={errors}
                disabled={pending}
              />
              <p className="mt-3 mb-6 text-sm text-gray-600 tabular-nums">
                {`異動後總金額（預覽）${formatDisplayAmount(amountPreview.totalAmount)}`
                  + ` ／ 原應付 ${formatDisplayAmount(detail.amountSummary?.totalAmount)}`}
              </p>
              <PurchaseAmendmentPaymentPlanEditor
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                currentPlan={currentPlan}
                currentPlanError={currentPlanError}
                originalTotal={originalPreview?.totalAmount}
                nextTotal={amountPreview.totalAmount}
                disabled={pending}
              />
              <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.miscPurchaseOrderDetail(id)}>取消</Link>
                </Button>
                {isEditMode && canUpdate ? (
                  <Button
                    type="button"
                    variant="destructive"
                    className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
                    disabled={pending}
                    onClick={handleDelete}
                  >
                    刪除草稿
                  </Button>
                ) : null}
                {canUpdate ? (
                  <Button type="submit" variant="outline" disabled={pending}>
                    {pending ? '處理中…' : '儲存草稿'}
                  </Button>
                ) : null}
                {canUpdate ? (
                  <Button
                    type="button"
                    disabled={pending}
                    onClick={handleSubmit(onSubmitDraft, markSubmitAttempted)}
                  >
                    提交審核
                  </Button>
                ) : null}
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMiscPurchaseOrderAmendmentPage;
